import React, {Fragment, useContext} from 'react';
import {NAVBAR_HEIGHT, PAGE_WIDTH} from '../../../shared/style/dimensions';
import glamorous from 'glamorous';
import Circular, {LARGE} from '../../../shared/library/indicators/indeterminate/circular';
import PropTypes from 'prop-types';
import Stacks from '../components/stacks';
import Integration from '../components/integration';
import BuiltFor from '../components/built-for';
import Reasons from '../components/reasons';
import Features from '../components/features';
import Alternatives from '../components/alternatives';
import Header from '../../../shared/library/header';
import Menu from '../components/menu';
import {toolFooterPresenter, normalizeOwner} from '../utils';
import Nav from '../components/nav';
import ToolBreadcrumbs from '../components/breadcrumbs';
import {Spinner} from '../components/shared/styles';
import Advert from '../../../shared/library/advert';
import * as AD_THEMES from '../../../shared/library/advert/themes';
import SecondaryFooter from '../../site/components/second-footer';
import Description from '../components/description';
import Keywords from '../components/keywords';
import {SigninDesktopModal} from '../../../shared/library/modals/signin';
import {adProperty, AD_LAYER, AD_CATEGORY, AD_FUNCTION} from '../../../shared/utils/ad';
import Followers from '../components/followers';
import {followers} from '../../../data/tool-profile/queries';
import {withAnalyticsPayload} from '../../../shared/enhancers/analytics-enhancer';
import {ASH} from '../../../shared/style/colors';
import Jobs from '../components/jobs';
import {PrivateModeContext} from '../../../shared/enhancers/private-mode-enchancer';
import PrivateStacks from '../components/private-stacks';
import DescriptionCard from '../../../shared/library/cards/description';
import PrivateDeveloperStacks from '../components/private-developer-stacks';
import PrivateContributedStacks from '../components/private-contributed-stacks';
import PrivateTeams from '../components/private-teams';
import PrivateFollowers from '../components/private-followers';
import {PROS} from '../../../shared/library/cards/pros-cons/row';
import {TOOL_ADOPTION_STAGE} from '../../../shared/constants/features';
import {featurePermissionCheck} from '../../../shared/utils/feature-permission-check';
import PrivateDecisions from '../components/private-decisions';
import {AD_IMPRESSION} from '../../../shared/constants/analytics';

const AdContainer = glamorous.div({
  width: 260,
  marginLeft: 20,
  marginBottom: 25
});

const AdLayout = glamorous.div({
  display: 'flex',
  position: 'relative'
});

const Aside = glamorous.aside({
  display: 'flex',
  marginTop: 25,
  minWidth: 280
});

const Grid = glamorous.div({
  flexGrow: 1
});

const Page = glamorous.div({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  marginBottom: 50
});

const TopContent = glamorous.div({
  display: 'flex',
  flexDirection: 'column',
  width: PAGE_WIDTH,
  height: '100%'
});

const Content = glamorous.main({
  display: 'flex',
  flexDirection: 'column',
  width: PAGE_WIDTH,
  height: '100%'
});

const NavOutter = glamorous.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  borderBottom: `2px solid ${ASH}`,
  position: 'sticky',
  top: NAVBAR_HEIGHT,
  zIndex: 100
});

const NavInner = glamorous.div({
  width: PAGE_WIDTH
});

const ToolProfile = ({tool, currentUser, loading, signin, contentGroup}) => {
  if (loading) {
    return (
      <Spinner>
        <Circular size={LARGE} />
      </Spinner>
    );
  }

  const privateMode = useContext(PrivateModeContext);
  const hasOwner = tool.company && tool.company.owners ? tool.company.owners.length > 0 : false;
  const isPackage = tool.type === 'Package';
  const isOwner =
    (tool.company && normalizeOwner(tool.company.owners)) ||
    (currentUser && currentUser.canIModerate) ||
    (currentUser && tool.company && tool.company.amIOwner);
  const showIntegrations = tool && tool.allToolIntegrations && tool.allToolIntegrations.any;
  const showBuiltFor =
    isPackage &&
    (tool.packageManager &&
      tool.packageManager.packageManagerTools &&
      tool.packageManager.packageManagerTools.length) >= 1;
  const showAlternatives = tool && tool.alternativeTools && tool.alternativeTools.edges.length >= 1;
  const showPosts = tool && tool.featuredPosts && tool.featuredPosts.any;
  const showPros = tool && tool[PROS] && tool[PROS].edges.length >= 1;
  const hasCompany = !!(tool.company && tool.company.stacks.length !== 0);
  const isCompaniesStacksUsing = tool && tool.companyStacksUsing && tool.companyStacksUsing.any;
  const isUserStacksUsing = tool && tool.userStacksUsing && tool.userStacksUsing.any;
  const hasFollowers = tool && tool.followers && tool.followers.count !== 0;
  const showPrivateStacks = tool && tool.privateStacks && tool.privateStacks.any;
  const showPrivateUsersUsingViaPersonalStacks =
    tool && tool.privateUsersUsingViaPersonalStacks && tool.privateUsersUsingViaPersonalStacks.any;
  const showPrivateUsersViaContributedStacks =
    tool && tool.privateUsersViaContributedStacks && tool.privateUsersViaContributedStacks.any;
  const showTeams = tool && tool.teams && tool.teams.any;
  const showPrivateFollowers = tool && tool.privateFollowers && tool.privateFollowers.count !== 0;
  const showJobs = tool.jobsCount > 0;
  const hasVersionRules = tool && tool.versionRules && tool.versionRules.any;
  const showStacks = isCompaniesStacksUsing || isUserStacksUsing;
  const showKeywords = isPackage && tool.keywords.length >= 1;
  const adLayer = adProperty(tool, AD_LAYER);
  const adCategory = adProperty(tool, AD_CATEGORY);
  const adFunction = adProperty(tool, AD_FUNCTION);
  const analyticsData = {name: tool.name, slug: tool.slug, contentGroup: contentGroup};
  const toolAdoptionStage = tool
    ? {...tool.adoptionStage, context: tool.adoptionStageContext}
    : null;
  const editPermission = privateMode && privateMode.permissions && privateMode.permissions.edit;
  const isEditStagesAllowed = privateMode?.adoptionStages?.some(stage => stage?.permissions?.edit);
  const stagePermission = featurePermissionCheck(privateMode, TOOL_ADOPTION_STAGE);

  return (
    <Fragment>
      <Page>
        <TopContent>
          <ToolBreadcrumbs tool={tool} />
          <Header
            logo={tool.imageUrl}
            title={tool.name}
            verified={tool.verified}
            openSource={tool.pressUrl}
            description={tool.title}
            twitter={tool.twitterUsername}
            website={tool.websiteUrl}
            packageUrl={tool.packageUrl}
            descFontSize={16}
            isCompany={hasCompany}
            company={tool.company}
            isPrivate={tool.private}
            showIcon={true}
            component={
              <Menu slug={tool.slug} name={tool.name} hasOwner={hasOwner} isOwner={isOwner} />
            }
            type={tool.type}
            packageManager={tool.packageManager}
            toolAdoptionStage={toolAdoptionStage}
            canEdit={editPermission || isEditStagesAllowed}
            stagePermission={stagePermission}
            tool={tool}
            hasVersionRules={hasVersionRules}
            metaDataStyle={{maxWidth: 550}}
            titleWidth={{maxWidth: 330}}
          />
        </TopContent>
        <NavOutter>
          <NavInner>
            <Nav
              service={tool}
              showIntegrations={isPackage ? showBuiltFor : showIntegrations}
              showAlternatives={showAlternatives}
              showStacks={
                (privateMode &&
                  (showPrivateStacks ||
                    showPrivateUsersUsingViaPersonalStacks ||
                    showPrivateUsersViaContributedStacks ||
                    showTeams)) ||
                showStacks
              }
              privateMode={privateMode}
              showPros={showPros}
              showFollowers={hasFollowers}
              showStats={false}
              showKeywords={showKeywords}
              showJobs={showJobs}
              showPosts={showPosts}
              jobsCount={tool.jobsCount}
              followers={followers}
              currentUser={currentUser}
            />
          </NavInner>
        </NavOutter>
        <Content>
          <AdLayout>
            <Grid>
              <Description tool={tool} />
              {showKeywords && <Keywords tool={tool} />}
              {((privateMode &&
                (showPrivateStacks ||
                  showPrivateUsersUsingViaPersonalStacks ||
                  showPrivateUsersViaContributedStacks ||
                  showTeams)) ||
                showStacks) && <DescriptionCard heading={`Who uses ${tool.name}?`} />}
              {privateMode && showPrivateStacks && (
                <PrivateStacks privateCompanyName={privateMode.name} tool={tool} />
              )}
              {privateMode && showPrivateUsersUsingViaPersonalStacks && (
                <PrivateDeveloperStacks tool={tool} privateCompanyName={privateMode.name} />
              )}
              {privateMode && showPrivateUsersViaContributedStacks && (
                <PrivateContributedStacks tool={tool} privateCompanyName={privateMode.name} />
              )}
              {privateMode && showTeams && (
                <PrivateTeams tool={tool} privateCompanyName={privateMode.name} />
              )}
              {privateMode && (
                <PrivateDecisions
                  tool={tool}
                  analyticsPayload={analyticsData}
                  privateCompanyName={privateMode && privateMode.name}
                />
              )}
              {privateMode && showPrivateFollowers && (
                <PrivateFollowers tool={tool} privateCompanyName={privateMode.name} />
              )}
              {showStacks && (
                <Stacks tool={tool} currentUser={currentUser} analyticsPayload={analyticsData} />
              )}
              {showBuiltFor && <BuiltFor tool={tool} />}
              {showIntegrations && (
                <Integration
                  tool={tool}
                  isPackage={isPackage}
                  currentUser={currentUser}
                  analyticsPayload={analyticsData}
                />
              )}
              <Reasons
                privateMode={privateMode}
                tool={tool}
                currentUser={currentUser}
                analyticsPayload={analyticsData}
                privateCompanyName={privateMode && privateMode.name}
                showPros={showPros}
                showPosts={showPosts}
              />
              {showJobs && <Jobs tool={tool} />}
              {tool.features && <Features tool={tool} />}
              {showAlternatives && <Alternatives tool={tool} />}
              {hasFollowers && (
                <Followers tool={tool} currentUser={currentUser} analyticsPayload={analyticsData} />
              )}
            </Grid>
            <Aside>
              <AdContainer>
                <div style={{position: 'sticky', top: 110 + NAVBAR_HEIGHT}}>
                  <Advert
                    placement="tool-profile-sticky"
                    objectType="Tool"
                    objectId={tool.slug}
                    contentGroupPage={contentGroup}
                    requestPath={tool.path}
                    layer={adLayer}
                    category={adCategory}
                    primaryFunction={adFunction}
                    maxWidth={260}
                    theme={AD_THEMES.COLUMN}
                    impressionType={AD_IMPRESSION}
                  />
                </div>
              </AdContainer>
            </Aside>
          </AdLayout>
        </Content>
        {signin && <SigninDesktopModal redirect={`/${tool.slug}`} />}
      </Page>
      {!tool.private && (
        <SecondaryFooter
          sections={toolFooterPresenter(tool, privateMode)}
          jobsLink={'/jobs'}
          jobsTitle={'Jobs'}
        />
      )}
    </Fragment>
  );
};

ToolProfile.propTypes = {
  tool: PropTypes.object,
  currentUser: PropTypes.object,
  loading: PropTypes.bool,
  signin: PropTypes.bool,
  contentGroup: PropTypes.string
};

export default withAnalyticsPayload({
  'page.name': 'Tool Profile',
  path: typeof window !== 'undefined' ? window.location.pathname : null,
  url: typeof window !== 'undefined' ? window.location.href : null,
  referrer: typeof document !== 'undefined' ? document.referrer : null
})(ToolProfile);
